import { useCallback, useRef } from 'react';
import { ACTIVE_COMPANIES_ONLY_QUERY, suggestionRowsFor, type Counterparty, type SaleQuantityRule, type SearchFn, type SuggestionRow, type SuggestionSource } from '@bms2/shared';
import { PAGES_PATH, PICKER_CAP, apiFetch } from '../records/api';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const SETTINGS_PATH = '/settings';
/** The catalog's categories and attribute types, read by the record pages' windows without the Settings pages that edit them (T3600; decision 10641). */
export const CATEGORIES_PATH = '/catalog/categories';
export const ATTRIBUTE_TYPES_PATH = '/catalog/attribute-types';
/** A person's own passkeys (decision 0351): the list, on the session's login alone; here so the shell's passkey offer reads it without the Account Settings page (T3600; decision 10641). */
export const PASSKEYS_PATH = '/auth/passkeys';
/** 🔒 The Address services list and each service's switch on Settings › Integrations › Maps (decision 7320). */
export const ADDRESS_SERVICES_PATH = '/settings/address-services';
export const AUDIT_RETENTION_PATH = '/audit/retention';
export const REMINDER_RULES_PATH = '/reminder-rules';
export const EMAIL_TEMPLATES_PATH = '/email-templates';
/** The From select's own options: the organisation's active logins with the person's name (decision 0569). */
export const SENDING_ADDRESSES_PATH = '/email-templates/sending-addresses';
/** Storage targets and backups (piece 9; decision 0265). */
export const STORAGE_PATH = '/settings/storage';
/** The three Security pages of piece 9 (decision 0345) and the Scheduled jobs card (decision 0346). */
export const AUDIT_SEARCH_PATH = '/audit/search';
export const SHARED_UNDER_NDA_PATH = '/files/shared-under-nda';
export const JOBS_PATH = '/jobs';
/** Piece 22's Settings › Accounting entries: the number series, the numbers issued by hand, and the two approval editors. */
export const ACCOUNTING_SETTINGS_PATH = '/settings/accounting';
/** Piece 23's five Settings › Accounting entries, read and written over the ledger's own routes. */
export const LEDGER_SETTINGS_PATH = '/accounting/settings';
/** Settings › Documents › Print Templates (piece 27; decision 0517): the one region editor and its preview. */
export const PRINT_TEMPLATES_PATH = '/settings/print-templates';
/** Every region of one template back to its family's defaults (piece 27; decision 0557). */
export function printTemplateResetPath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}/reset-regions`;
}
/** 🔒 One template's whole region set, saved in one request by the page's one Save (decision 0567). */
export function printTemplateRegionsPath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}/regions`;
}
/**
 * 🔒 The sheet a draft would make, asked for while a person types (decision
 * 0571). It carries the same region set the Save carries and writes nothing.
 *
 * 🔒 T172: the section rail's open section rides along as `?highlight=`, so
 * the sheet the pane draws carries every region's box, that section's in blue
 * (decisions 0655, 0696). Absent — Region boxes turned off — the address is
 * exactly what it always was and the sheet carries no box.
 */
export function printTemplatePreviewPath(templateId: string, highlightSection?: string | null, envelopeProfileId?: string | null, checkStock = false, bankAccountId: string | null = null): string {
  /* 🔒 T191: the envelope the preview is laid over rides as `?envelope=`, drawn by the server into the sheet (decision 0662). */
  /* 🔒 T3126: a check template's stock rides as `?checkStock=1`, and the bank account whose stock it is as `&bankAccountId=` (decisions 0700, 7630). */
  const query = [
    ...(highlightSection ? [`highlight=${encodeURIComponent(highlightSection)}`] : []),
    ...(envelopeProfileId ? [`envelope=${encodeURIComponent(envelopeProfileId)}`] : []),
    ...(checkStock ? ['checkStock=1'] : []),
    ...(checkStock && bankAccountId ? [`bankAccountId=${encodeURIComponent(bankAccountId)}`] : []),
  ];
  return `${PRINT_TEMPLATES_PATH}/${templateId}/preview${query.length > 0 ? `?${query.join('&')}` : ''}`;
}
/** 🔒 The windowed envelopes the preview can be laid over (T191; decision 0662), seeded and read here. */
export const PRINT_ENVELOPE_PROFILES_PATH = `${PRINT_TEMPLATES_PATH}/envelope-profiles`;
/** 🔒 Settings › Documents › Envelopes (T208; decision 0699): every profile, hidden ones included, edited in place and added to. */
export const ENVELOPE_PROFILES_SETTINGS_PATH = '/settings/envelope-profiles';
/**
 * 🔒 The sample sheet one template prints, as the sheet itself (decision 0571).
 * A person reads it in the application's own viewer before copying the format.
 */
export function printTemplatePreviewPdfPath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}/preview.pdf`;
}
/** 🔒 Duplicate to… (T192; decision 0658): one template's layout onto another document type of its family, or every other one. */
export function printTemplateDuplicatePath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}/duplicate`;
}
/** 🔒 T193 (decision 0663): this template becomes its document type's global default, the old one ceasing to be. */
export function printTemplateMakeDefaultPath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}/make-default`;
}
/** 🔒 T404: a template's name, changed in place from its card or its own page. */
export function printTemplateRenamePath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}/rename`;
}
/** 🔒 T436 (decision 1122): one template, which a DELETE removes with its rows and regions. */
export function printTemplatePath(templateId: string): string {
  return `${PRINT_TEMPLATES_PATH}/${templateId}`;
}
/** A customer's own format, raised from the list's create (piece 27). */
export const PRINT_TEMPLATE_FOR_COMPANY_PATH = `${PRINT_TEMPLATES_PATH}/for-company`;
/** 🔒 Where the list stands and where one template's page stands beneath it (decision 0567). */
export const PRINT_TEMPLATES_ROUTE = '/settings/documents/print-templates';
export function printTemplateRoute(templateId: string): string {
  return `${PRINT_TEMPLATES_ROUTE}/${templateId}`;
}

/** Settings › Organization › Email domains: the OrganizationDomain rows the interlock of decision 0157 reads. */
export const ORGANIZATION_DOMAINS_PATH = '/settings/organization/domains';

/** Settings › HR › Work Calendar's Clock devices section: every registered device and the gated sites (decision 1607). */
export const CLOCK_DEVICES_PATH = '/settings/clock-devices';

/** Retires one clock device; its row stays, inactive (decision 1607). */
export function clockDeviceRetirePath(id: string): string {
  return `${CLOCK_DEVICES_PATH}/${encodeURIComponent(id)}/retire`;
}

/** Settings › HR › Work Calendar's Holidays section: one year's rows (`?year=`), Add a holiday (decision 1390). */
export const HOLIDAYS_PATH = '/settings/holidays';
/** Generates the federal holidays of the year named in the body; a date already holding one is left alone (decision 1720). */
export const HOLIDAYS_GENERATE_PATH = '/settings/holidays/generate';
/** Settings › HR › Work Calendar's Business hours section: its read, and Set Hours over the same path (decision 3860). */
export const BUSINESS_HOURS_PATH = '/settings/business-hours';
/** The Workweek section: its read, and Change the Workweek over the same path (decision 3860). */
export const WORKWEEKS_PATH = '/settings/workweeks';
/** Removes one holiday for a typed reason (decision 1390). */
export function holidayRemovePath(id: string): string {
  return `${HOLIDAYS_PATH}/${encodeURIComponent(id)}/remove`;
}

/** The organisation's one logo, uploaded once and drawn in Region 1 of every print template (decision 0575). */
export const ORGANIZATION_LOGO_PATH = '/settings/organization/logo';

/** Settings › Organization › Organization Profile: the organisation's own facts, and the one audited write over its row (decisions 1393 to 1396). */
export const ORGANIZATION_PROFILE_PATH = '/settings/organization/profile';

/**
 * The Company search a Settings picker runs, over the list read the record pages already use. A picker on a new document passes
 * `activeOnly`, and the server leaves out a company that is not Active (decision 7530); nothing is filtered here.
 */
export function useCompanySearch(apiBaseUrl: string, role: Counterparty | 'manufacturer' | null = null, options: { activeOnly?: boolean; onRefused?: () => void } = {}) {
  const activeOnly = options.activeOnly === true;
  const onRefused = options.onRefused;
  return useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      // 🔒 A side named asks the server for that side alone (T184; decision 0637); nothing is filtered here.
      const side = `picker=true&${role ? `role=${role}&` : ''}${activeOnly ? `${ACTIVE_COMPANIES_ONLY_QUERY}&` : ''}`;
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/CompanyRecord?${side}${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) {
        // A refused read (403) is told to the picker that asked, which may say why the list is empty; every other picker ignores it.
        if (response.status === 403) onRefused?.();
        return [];
      }
      const body = (await response.json()) as { rows: SuggestionSource[] };
      return suggestionRowsFor('CompanyRecord', body.rows);
    },
    [apiBaseUrl, role, activeOnly, onRefused],
  );
}

/** The Person search a picker runs, over the list read the record pages already use (T362, T540). */
export function usePersonSearch(apiBaseUrl: string): SearchFn {
  return useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/PersonRecord?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: SuggestionSource[] };
      return suggestionRowsFor('PersonRecord', body.rows);
    },
    [apiBaseUrl],
  );
}

/** The sales or the purchase side an Item pick fills (decision 0181): the price and the description read off it. */
export type ItemPickerSide = 'sales' | 'purchase';

/**
 * 🔒 The Item search nine document forms wrote separately (T540): the search
 * itself, and the maps it seeds as a picked row arrives — unit, unit id, SKU,
 * description and price for the side named, which rows are Services, and the
 * sale guards a sales host corrects a quantity by (decision 1526). A
 * form reads only the maps it needs; the hook always fills all of them, so a
 * form that starts reading one more never grows a second private copy.
 */
export function useItemPicker(apiBaseUrl: string, side: ItemPickerSide) {
  const units = useRef(new Map<string, string>());
  const unitIds = useRef(new Map<string, string>());
  const prices = useRef(new Map<string, number>());
  const skus = useRef(new Map<string, string>());
  const descriptions = useRef(new Map<string, string>());
  const services = useRef(new Set<string>());
  // 🔒 The sale guards a sales host hands the grid (decision 1526): the minimum and the increment in the Item's own unit, kept only where one is set.
  const quantityRules = useRef(new Map<string, SaleQuantityRule>());
  const itemSearch = useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/Item?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: SuggestionSource[] };
      for (const row of body.rows) {
        const id = String(row.id);
        units.current.set(id, String(row.unit ?? ''));
        skus.current.set(id, String(row.sku ?? ''));
        descriptions.current.set(id, String((side === 'sales' ? row.salesDescription : row.purchaseDescription) ?? ''));
        if (typeof row.unitId === 'string') unitIds.current.set(id, row.unitId);
        const price = side === 'sales' ? row.salesPrice : row.purchasePrice;
        if (typeof price === 'number') prices.current.set(id, price);
        if (row.type === 'Service') services.current.add(id);
        const minimum = typeof row.minimumSaleQuantity === 'number' ? row.minimumSaleQuantity : null;
        const increment = typeof row.saleQuantityIncrement === 'number' ? row.saleQuantityIncrement : null;
        if (minimum !== null || increment !== null) quantityRules.current.set(id, { minimum, increment, unit: String(row.unit ?? '') });
        else quantityRules.current.delete(id);
      }
      return suggestionRowsFor('Item', body.rows);
    },
    [apiBaseUrl, side],
  );
  /**
   * 🔒 The lookups an item table is handed (decision 10711; T954), spread into it as `{...pickerProps()}`. A sales form
   * passes `{ saleGuards: true }` for the Item's sale quantity guards (decision 1526), and only the Quote and the Sales
   * Order pass `{ service: true }` so a Service pick draws the budget cell (decision 0860; piece 22).
   */
  const pickerProps = useCallback(
    (options: { service?: boolean; saleGuards?: boolean } = {}): ItemPickerTableProps => ({
      itemSearch,
      seedRateCents: (id) => prices.current.get(id) ?? null,
      unitOf: (id) => units.current.get(id) ?? null,
      skuOf: (id) => skus.current.get(id) || null,
      descriptionOf: (id) => descriptions.current.get(id) || null,
      ...(options.saleGuards ? { quantityRuleOf: (id: string) => quantityRules.current.get(id) ?? null } : {}),
      ...(options.service ? { serviceOf: (id: string) => services.current.has(id) } : {}),
    }),
    [itemSearch],
  );
  return { itemSearch, units, unitIds, prices, skus, descriptions, services, quantityRules, pickerProps };
}

/** The lookups of `useItemPicker` an item table reads; `quantityRuleOf` and `serviceOf` are present only where the form asks for them. */
export interface ItemPickerTableProps {
  itemSearch: (query: string, signal: AbortSignal) => Promise<SuggestionRow[]>;
  seedRateCents: (itemId: string) => number | null;
  unitOf: (itemId: string) => string | null;
  skuOf: (itemId: string) => string | null;
  descriptionOf: (itemId: string) => string | null;
  quantityRuleOf?: (itemId: string) => SaleQuantityRule | null;
  serviceOf?: (itemId: string) => boolean;
}
